import { useState } from 'react'
import bgSpeed from '../../assets/images/bg-speed.png'
import ServiceButton from '../ui/ServiceButton'
import robot from '../../assets/images/robot.png'

function validateEmail(email: string) {
  // Simple email regex for demonstration
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  })

  const [errors, setErrors] = useState<{ name?: string; email?: string; message?: string }>({})
  const [touched, setTouched] = useState<{ name?: boolean; email?: boolean; message?: boolean }>({})
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const validate = (fieldValues = formData) => {
    let temp: typeof errors = {}

    if (!fieldValues.name || fieldValues.name.trim().length < 2) {
      temp.name = 'Please enter your name (at least 2 characters).'
    }

    if (!fieldValues.email) {
      temp.email = 'Please enter your email address.'
    } else if (!validateEmail(fieldValues.email)) {
      temp.email = 'Please enter a valid email address.'
    }

    if (!fieldValues.message || fieldValues.message.trim().length < 10) {
      temp.message = 'Message should be at least 10 characters.'
    }

    setErrors(temp)
    return Object.keys(temp).length === 0
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setTouched(prev => ({
      ...prev,
      [name]: true
    }))
    // Validate on change for the specific field
    validate({ ...formData, [name]: value })
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target
    setTouched(prev => ({
      ...prev,
      [name]: true
    }))
    validate(formData)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      setSubmitSuccess(true)
      setErrors({})
      // Handle form submission logic here (e.g., send to API)
      console.log('Form submitted:', formData)
      // Optionally reset form
      setFormData({ name: '', email: '', message: '' })
      setTouched({})
      setTimeout(() => setSubmitSuccess(false), 4000)
    } else {
      setSubmitSuccess(false)
    }
  }

  return (
    <section id="contact" className="bg-white">
      {/* Contact Form Section */}
      <div className="py-20 px-4">
        <div className="container sm:px-10 px-3  mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left - Robot Image */}
            <div className="relative">
              <img
                src={robot}
                alt="Speedyssey delivery robot"
                className="w-full h-[300px] sm:h-[500px]"
              />
            </div>

            {/* Right - Contact Form */}
            <div>
              <h2 
                className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-8 leading-tight"
                style={{ fontFamily: 'Antonio, sans-serif' }}
              >
                NEED HELP?<br />
                WE'RE HERE FOR <span className="text-[#F55109]">YOU</span>
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-6 py-4 bg-gray-100 border-0 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 transition-all duration-300 ${
                      errors.name && touched.name
                        ? 'focus:ring-red-500 ring-2 ring-red-400'
                        : 'focus:ring-[#F55109]'
                    }`}
                    required
                    aria-invalid={!!errors.name}
                    aria-describedby="contact-name-error"
                  />
                  {errors.name && touched.name && (
                    <div
                      id="contact-name-error"
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.name}
                    </div>
                  )}
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-6 py-4 bg-gray-100 border-0 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 transition-all duration-300 ${
                      errors.email && touched.email
                        ? 'focus:ring-red-500 ring-2 ring-red-400'
                        : 'focus:ring-[#F55109]'
                    }`}
                    required
                    aria-invalid={!!errors.email}
                    aria-describedby="contact-email-error"
                  />
                  {errors.email && touched.email && (
                    <div
                      id="contact-email-error"
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.email}
                    </div>
                  )}
                </div>

                <div>
                  <textarea
                    name="message"
                    placeholder="Message"
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-6 py-4 bg-gray-100 border-0 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 transition-all duration-300 resize-none ${
                      errors.message && touched.message
                        ? 'focus:ring-red-500 ring-2 ring-red-400'
                        : 'focus:ring-[#F55109]'
                    }`}
                    required
                    aria-invalid={!!errors.message}
                    aria-describedby="contact-message-error"
                  />
                  {errors.message && touched.message && (
                    <div
                      id="contact-message-error"
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.message}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className={`px-8 py-4 ${
                    Object.keys(errors).length === 0 ? 'bg-gray-400 hover:bg-gray-500' : 'bg-gray-300'
                  } text-white font-semibold rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-500`}
                  disabled={Object.keys(errors).length > 0}
                >
                  Send Message
                </button>
                {submitSuccess && (
                  <div className="text-green-600 text-center font-medium mt-2">
                    Thank you! Your message has been sent.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div
        className="py-20 px-4 bg-cover bg-center relative"
        style={{
          backgroundImage: `url(${bgSpeed})`,
          minHeight: '600px',
        }}
      >
        {/* <div className="absolute inset-0 bg-white/80 pointer-events-none" aria-hidden="true" /> */}
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#2C2C2C] mb-8 leading-tight"
            style={{ fontFamily: 'Antonio, sans-serif' }}
          >
            SCALE YOUR <span className="text-[#F55109]">BUSINESS</span> WITH SMART
            LOGISTICS
          </h2>

          <p className="text-gray-600 text-lg leading-relaxed mb-12 max-w-3xl mx-auto">
            Speedyssey gives you the infrastructure and flexibility to move goods at scale, 
            with real-time insights, dedicated support, and predictable delivery windows.
          </p>

          <div className="flex justify-center">
            <ServiceButton
              title="Let's Talk Logistics"
              className="w-auto"
              onClick={() => {
                window.open(
                  'https://outlook.office365.com/book/LetsTalk@Speedyssey.onmicrosoft.com/?ismsaljsauthenabled=true',
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact